import { Menu, UnstyledButton, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconLanguage } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { Locale } from "@iso27001/shared";
import { changeLocale } from "../i18n/index.js";
import { useAuth } from "../auth/AuthContext.js";

const LOCALES: Locale[] = ["de", "en"];

/**
 * Persists to the server for a signed-in user so the choice follows them
 * between devices, and to localStorage always so an anonymous reader of the
 * public documentation keeps their choice too.
 */
export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  // changeLocale can reject (server persist failed): localStorage and the
  // visible language have already changed by then, so silently discarding
  // the rejection (the old `void changeLocale(...)`) would leave the user
  // believing their choice is saved when the account was never updated —
  // and resolveLocale ranks the account's stored locale above this
  // browser's, so it would silently revert on the next session elsewhere.
  // Surface it instead of hiding it.
  function handleSelect(locale: Locale) {
    changeLocale(locale, { persistToServer: !!user }).catch(() => {
      notifications.show({ message: t("common.language.saveFailed"), color: "red" });
    });
  }

  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <UnstyledButton aria-label={t("common.language.label")}>
          <IconLanguage size={18} />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {LOCALES.map((l) => (
          <Menu.Item
            key={l}
            onClick={() => handleSelect(l)}
            fw={i18n.language === l ? 700 : 400}
          >
            <Text size="sm">{t(`common.language.${l}` as const)}</Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
